import { useEffect, useState } from "react";
import type { CameraPreset, SceneState } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { notice, useDomainState, useEditorStore, useUi } from "../../../../state/editor/useEditor";
import { pickFile } from "../../../../util/download";
import { Icon } from "../../../kit";
import { useManifest } from "../../library/useManifest";
import { reconcileBackdrop, resyncAfterLook, sceneLookState, useSceneLooks, type SceneLook } from "../../library/sceneLooks";
import { BASIC_CONTROLS, type SwatchDef } from "../macros";
import type { AppliedStep, LookChange } from "../applyEngine";
import { LOOKS, loadMyLooks, saveMyLooks, type Look, type MyLook } from "../looks";
import { useBasicEngine } from "../useBasicEngine";
import { ActionRow, Note, Section, Swatch, Tile } from "./ui";

const fail = (e: Error) => notice(`Couldn't apply that: ${e.message}`);

// ─────────────────────────────────────────────────────────────── Scenes

/** Rendered thumbnails ship with the app (scripts/render-look-thumbs.mjs). */
const thumbUrl = (id: string) => `/looks/${id}.webp`;

/**
 * Scenes — the reference's "Scene Presets": Basic's own one-click looks
 * (look-field values, undoable), "Match a photo", the looks you saved, then
 * the scene library Advanced's Looks panel offers (whole captured scenes).
 */
export function ScenesPanel() {
  const { client } = useViewer();
  const store = useEditorStore();
  const engine = useBasicEngine();
  const [ui, patchUi] = useUi();
  const [active, setActive] = useState<string | null>(null);
  /** The library scene in force (shared with Advanced's Looks panel). One
   *  ring at a time across both grids: a look replaces a scene and back. */
  const [scene, setScene] = useState<string | null>(sceneLookState.appliedId);
  const [busy, setBusy] = useState<string | null>(null);
  const [mine, setMine] = useState<MyLook[]>(() => loadMyLooks());

  const applyValues = async (id: string, label: string, values: LookChange[], fx?: Look["fx"]) => {
    if (busy) return;
    setBusy(id);
    try {
      // The backdrop's tone-mapping flag first, so the look's white lands as
      // #ffffff (light looks) and the dark ones keep their filmic backdrop.
      if (fx) store.setState("postFX", await client.ring.setPostFX(fx));
      await engine.setValues(values, `Look: ${label}`, undefined, `look:${id}`);
      await store.refresh("scene");
      setActive(id);
      sceneLookState.appliedId = null;
      setScene(null);
    } catch (e) {
      fail(e as Error);
    } finally {
      setBusy(null);
    }
  };

  const saveCurrent = () => {
    const values = Object.entries(engine.currentValues())
      .filter(([k]) => !k.startsWith("gem.") && !k.startsWith("metal."))
      .map(([key, value]) => ({ key, value }));
    const look: MyLook = { id: `mine-${Date.now().toString(36)}`, label: `My look ${mine.length + 1}`, values, background: null };
    const next = [look, ...mine];
    setMine(next);
    saveMyLooks(next);
  };

  const removeMine = (id: string) => {
    const next = mine.filter((l) => l.id !== id);
    setMine(next);
    saveMyLooks(next);
  };

  return (
    <div data-basic-looks>
      {!ui.coachDone ? (
        <div className="mb-4 rounded-[10px] border border-ed-border bg-ed-panel-2 px-3 py-2.5" data-coach>
          <p className="text-[13px] font-semibold text-ed-text">Three steps to a finished photo</p>
          <ol className="mt-1 list-inside list-decimal text-[12px] leading-[18px] text-ed-text-2">
            <li>Pick a look below</li>
            <li>Choose the metal and stones</li>
            <li>Press <b className="font-semibold text-ed-text">Export</b> and pick where it's going</li>
          </ol>
          <button onClick={() => patchUi({ coachDone: true })} className="mt-1.5 text-[12px] font-semibold text-ed-text underline underline-offset-2 hover:text-black">
            Got it
          </button>
        </div>
      ) : null}

      <div className="mb-5">
        <ActionRow
          icon="auto_awesome"
          label="Match a photo"
          sub="Show the AI a photo — it sets the look for you"
          onClick={() => patchUi({ leftPanel: "aiMatch", lastBasicLeftPanel: "aiMatch" })}
          data-basic-ai-promo
        />
      </div>

      <Section label="Looks">
        <div className="grid grid-cols-2 gap-2">
          {LOOKS.map((l) => (
            <Tile
              key={l.id}
              tall
              label={l.label}
              sub={l.use}
              on={active === l.id}
              busy={busy === l.id}
              disabled={!!busy && busy !== l.id}
              thumbStyle={{ background: l.swatch }}
              thumb={<Thumb src={thumbUrl(l.id)} />}
              onClick={() => void applyValues(l.id, l.label, l.values, l.fx)}
              data-look={l.id}
            />
          ))}
        </div>
      </Section>

      <Section
        label="Your looks"
        aside={
          <button onClick={saveCurrent} className="flex items-center gap-1 text-[12px] font-semibold text-ed-gold hover:underline" data-save-look>
            <Icon name="add" size={16} /> Save this look
          </button>
        }
      >
        {mine.length === 0 ? (
          <Note>Tune a look you like, then save it here to use again on every piece.</Note>
        ) : (
          <div className="flex flex-col gap-1.5">
            {mine.map((l) => (
              <div key={l.id} className={`flex items-center gap-2 rounded-[10px] border px-3 py-2 ${active === l.id ? "border-ed-gold bg-ed-active" : "border-ed-border"}`}>
                <button className="min-w-0 flex-1 truncate text-left text-[13px] font-medium text-ed-text" onClick={() => void applyValues(l.id, l.label, l.values)}>
                  {l.label}
                </button>
                <button title="Delete this look" aria-label={`Delete ${l.label}`} onClick={() => removeMine(l.id)} className="grid h-7 w-7 place-items-center rounded-md text-ed-text-3 hover:bg-ed-hover hover:text-ed-text">
                  <Icon name="delete" size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </Section>

      <LibraryScenes
        active={scene}
        onActive={(sceneId) => {
          setScene(sceneId);
          if (sceneId) setActive(null);
        }}
      />
    </div>
  );
}

/**
 * The scene library (library/sceneLooks.ts, shared with Advanced): a whole
 * captured scene — camera, effects, floor and the scene's own materials —
 * replayed over the loaded piece. Basic makes it ONE Undo step: the scene as
 * it stood is captured first (`exportVjson`, the same snapshot "Save current
 * as Look" stores) and Undo loads it back.
 */
function LibraryScenes({ active, onActive }: { active: string | null; onActive: (id: string | null) => void }) {
  const { client } = useViewer();
  const r = client.ring;
  const store = useEditorStore();
  const engine = useBasicEngine();
  const looks = useSceneLooks();
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  /** A scene replays every part's material too, so the engine's per-part
   *  values are re-read along with the slices. */
  const settle = () => Promise.all([resyncAfterLook(store), store.refresh("groups")]);

  /** Land a scene under the shared backdrop rule; say so (plainly) only when
   *  the rule was switched off and the scene greys the background. */
  const land = async (look: SceneLook) => {
    await r.loadVjsonUrl(look.url);
    const said = await reconcileBackdrop(r, sceneLookState.keepBackdropFlat, look.label);
    sceneLookState.appliedId = look.id;
    onActive(look.id);
    setNote(said && !sceneLookState.keepBackdropFlat ? `“${look.label}” greys the background in every picture.` : null);
    await settle();
  };

  const apply = async (look: SceneLook) => {
    if (busy) return;
    setBusy(look.id);
    try {
      // Captured BEFORE the load; without it the step cannot be undone, and
      // the scene is still worth applying, so a failed capture is not fatal.
      const before = await r.exportVjson({ pretty: false }).catch(() => null);
      await land(look);
      if (before) {
        engine.recordExternal({
          label: `Scene: ${look.label}`,
          undo: async () => {
            await r.loadVjsonFile(new File([before], "before.vjson", { type: "application/json" }));
            sceneLookState.appliedId = null;
            onActive(null);
            await settle();
          },
          redo: () => land(look),
        });
      }
    } catch (e) {
      fail(e as Error);
    } finally {
      setBusy(null);
    }
  };

  if (looks.length === 0) return null;
  return (
    <Section label="Scenes">
      <div className="grid grid-cols-2 gap-2">
        {looks.map((l) => (
          <Tile
            key={l.id}
            tall
            label={l.label}
            on={active === l.id}
            busy={busy === l.id}
            disabled={!!busy && busy !== l.id}
            thumbStyle={l.thumb ? { backgroundImage: `url("${l.thumb}")`, backgroundSize: "cover", backgroundPosition: "center" } : { backgroundImage: "linear-gradient(135deg,#f3ece0,#d8c9ad)" }}
            thumb={l.thumb ? undefined : <Icon name="style" size={22} className="text-ed-text-2" />}
            onClick={() => void apply(l)}
            data-scene-look={l.id}
          />
        ))}
      </div>
      {note ? <div className="mt-2"><Note>{note}</Note></div> : null}
      <p className="mt-2 text-[12px] leading-[17px] text-ed-text-2">
        A scene sets the camera, light, floor and finish all at once, over your piece.
      </p>
    </Section>
  );
}

/** A rendered preview (scripts: ../parity/studio-redesign/render-look-thumbs.mjs),
 *  hidden until it loads so the tile's own fill shows first. */
function Thumb({ src }: { src: string }) {
  const [ok, setOk] = useState(false);
  return (
    <img
      src={src}
      alt=""
      className={`h-full w-full object-cover transition-opacity ${ok ? "opacity-100" : "opacity-0"}`}
      onLoad={() => setOk(true)}
      onError={() => setOk(false)}
      draggable={false}
    />
  );
}

// ─────────────────────────────────────────────────────────────── Background

/** A library backdrop: a gradient (static manifest — it carries its two
 *  colours) or a picture (a curated preset — it carries a thumbnail). */
interface BackdropEntry { id: string; label: string; file: string; thumb?: string; top?: string; bottom?: string }

/** Pictures shown before "Show all". */
const PICTURES_SHOWN = 9;

const FLOORS: { id: string; label: string; icon: string; values: LookChange[] }[] = [
  { id: "none", label: "No floor", icon: "block", values: [{ key: "shadow.enabled", value: false }, { key: "floor.reflection", value: false }] },
  {
    id: "shadow", label: "Soft shadow", icon: "blur_on",
    values: [{ key: "shadow.enabled", value: true }, { key: "shadow.darkness", value: 0.5 }, { key: "shadow.softness", value: 0.7 }, { key: "floor.reflection", value: false }],
  },
  {
    id: "glossy", label: "Glossy", icon: "water_drop",
    values: [{ key: "shadow.enabled", value: true }, { key: "floor.reflection", value: true }, { key: "floor.reflectionStrength", value: 0.6 }, { key: "floor.reflectionBlur", value: 0.4 }],
  },
  {
    id: "mirror", label: "Mirror", icon: "flip",
    values: [{ key: "shadow.enabled", value: true }, { key: "floor.reflection", value: true }, { key: "floor.reflectionStrength", value: 1.2 }, { key: "floor.reflectionBlur", value: 0.1 }],
  },
];

const humanize = (s: string) => s.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export function BackdropPanel() {
  const { client } = useViewer();
  const r = client.ring;
  const store = useEditorStore();
  const engine = useBasicEngine();
  const scene = useDomainState("scene") as SceneState | undefined;
  const [busy, setBusy] = useState<string | null>(null);
  const [floor, setFloor] = useState<string | null>(null);
  const bg = BASIC_CONTROLS.find((c) => c.id === "background") as SwatchDef;
  const colours = Array.isArray(bg?.options) ? bg.options : [];
  const { items: library, urlFor, thumbFor } = useManifest<BackdropEntry>("/backgrounds/manifest.json", "background");
  const [allPictures, setAllPictures] = useState(false);
  // The shape comes from the FILE, never the id (an API row's id is a database
  // id) — the same rule Advanced's Background panel follows.
  const isRadial = (g: BackdropEntry) => /bg_radial/i.test(g.file ?? "");
  const gradients = library.filter((g) => g.top && g.bottom);
  const pictures = library.filter((g) => !(g.top && g.bottom));
  const resync = () => void store.refresh("scene");
  const cur = engine.read("light.background");
  const curHex = typeof cur === "string" ? cur.toLowerCase() : null;
  const source = scene?.backgroundSource;
  /** The library item now on screen, read back from the scene. */
  const onScreen = (g: BackdropEntry) => source === "image" && !!scene?.backgroundImageUrl && scene.backgroundImageUrl === urlFor(g);

  const job = async (id: string, fn: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(id);
    try {
      await fn();
    } catch (e) {
      fail(e as Error);
    } finally {
      setBusy(null);
      resync();
    }
  };

  return (
    <div data-basic-backdrop>
      <Section label="Colour">
        <div className="grid grid-cols-5 gap-1">
          {colours.map((o) => (
            <Swatch
              key={o.id}
              label={o.label}
              fill={o.hex}
              on={source !== "image" && source !== "transparent" && curHex === o.hex.toLowerCase()}
              disabled={engine.busy}
              onClick={() => void job(o.id, () => engine.setValues([{ key: "light.background", value: o.hex }], `Background: ${o.label}`, undefined, "background"))}
              data-basic-swatch={o.id}
            />
          ))}
        </div>
      </Section>

      <Section label="Gradient">
        <div className="grid grid-cols-4 gap-1.5">
          {gradients.map((g) => (
            <button
              key={g.id}
              title={humanize(g.label)}
              aria-label={humanize(g.label)}
              aria-pressed={onScreen(g)}
              disabled={!!busy}
              onClick={() => void job(`grad:${g.id}`, () => r.setBackgroundImageUrl(urlFor(g)))}
              className="h-12 rounded-lg border border-ed-border transition-shadow hover:shadow-[0_0_0_2px_#fff,0_0_0_3.5px_#8a8a85] disabled:opacity-50"
              style={{
                background: isRadial(g) ? `radial-gradient(circle at 50% 42%, ${g.top}, ${g.bottom})` : `linear-gradient(${g.top}, ${g.bottom})`,
                boxShadow: onScreen(g) ? "0 0 0 2px #fff, 0 0 0 4px #946f2e" : undefined,
              }}
              data-gradient={g.id}
            />
          ))}
        </div>
      </Section>

      {pictures.length > 0 ? (
        <Section
          label="Pictures"
          aside={
            pictures.length > PICTURES_SHOWN ? (
              <button onClick={() => setAllPictures((v) => !v)} className="text-[12px] font-semibold text-ed-text-2 hover:text-ed-text">
                {allPictures ? "Show fewer" : `Show all ${pictures.length}`}
              </button>
            ) : undefined
          }
        >
          <div className="grid grid-cols-3 gap-2">
            {(allPictures ? pictures : pictures.slice(0, PICTURES_SHOWN)).map((g) => (
              <Tile
                key={g.id}
                label={humanize(g.label)}
                on={onScreen(g)}
                busy={busy === `pic:${g.id}`}
                disabled={!!busy && busy !== `pic:${g.id}`}
                thumbStyle={{ backgroundImage: `url("${g.thumb ? thumbFor(g) : urlFor(g)}")`, backgroundSize: "cover", backgroundPosition: "center" }}
                onClick={() => void job(`pic:${g.id}`, () => r.setBackgroundImageUrl(urlFor(g)))}
                data-backdrop-picture={g.id}
              />
            ))}
          </div>
        </Section>
      ) : null}

      <Section label="More">
        <div className="flex flex-col gap-1.5">
          <ActionRow
            icon="texture"
            label="See-through background"
            sub="For a PNG you can place on any page"
            busy={busy === "transparent"}
            trailing={source === "transparent" ? <Icon name="check" size={18} className="text-ed-gold" /> : undefined}
            onClick={() => void job("transparent", () => Promise.resolve(r.setTransparentBackground()))}
            data-transparent
          />
          <ActionRow
            icon="add_photo_alternate"
            label="Use my photo"
            sub="A lifestyle shot or your shop's backdrop"
            busy={busy === "photo"}
            onClick={() => void job("photo", () => pickFile("image/*", (f) => r.setBackgroundImageFile(f)))}
          />
        </div>
      </Section>

      <Section label="Floor">
        <div className="grid grid-cols-2 gap-2">
          {FLOORS.map((f) => (
            <Tile
              key={f.id}
              label={f.label}
              on={floor === f.id}
              disabled={engine.busy}
              tall
              thumbStyle={{ background: "#ffffff" }}
              thumb={<Thumb src={`/floors/${f.id}.webp`} />}
              onClick={() =>
                void engine
                  .setValues(f.values, `Floor: ${f.label}`, undefined, `floor:${f.id}`)
                  .then(() => setFloor(f.id))
                  .catch(fail)
              }
              data-floor={f.id}
            />
          ))}
        </div>
      </Section>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────── Camera

/**
 * `distance` scales the viewer's own framing for that preset. The panels float
 * OVER the canvas (the canvas never resizes for them), so the viewer frames the
 * ring for the whole canvas and a shot at 1× fills the part the panels leave
 * visible edge to edge. 1.45× sits it in that visible part with room around
 * it, the way the approved mockup shows it. Close-up keeps the viewer's 1×.
 * Exports are unaffected: fillFrame.ts frames every photo itself.
 */
const SHOTS: { id: string; label: string; preset: CameraPreset; distance: number; icon: string }[] = [
  { id: "hero", label: "Hero ¾", preset: "threeQuarter", distance: 1.45, icon: "view_in_ar" },
  { id: "front", label: "Front", preset: "front", distance: 1.45, icon: "crop_portrait" },
  { id: "side", label: "Side", preset: "right", distance: 1.45, icon: "crop_landscape" },
  { id: "top", label: "Top", preset: "top", distance: 1.45, icon: "crop_square" },
  { id: "close", label: "Close-up", preset: "threeQuarter", distance: 1, icon: "zoom_in" },
];

/** Shot ids shared with the view bar, so both show the same "current" shot. */
export const SHOT_EVENT = "studio:shot";

export async function takeShot(ring: { setCameraPreset(p: CameraPreset): unknown; zoomCamera(f: number): unknown }, id: string) {
  const s = SHOTS.find((x) => x.id === id);
  if (!s) return;
  await ring.setCameraPreset(s.preset);
  if (s.distance !== 1) await ring.zoomCamera(s.distance);
  window.dispatchEvent(new CustomEvent(SHOT_EVENT, { detail: id }));
}

export function useCurrentShot(): string | null {
  const [shot, setShot] = useState<string | null>(null);
  useEffect(() => {
    const on = (e: Event) => setShot((e as CustomEvent<string>).detail);
    window.addEventListener(SHOT_EVENT, on);
    return () => window.removeEventListener(SHOT_EVENT, on);
  }, []);
  return shot;
}

export function CameraPanel() {
  const { client } = useViewer();
  const r = client.ring;
  const shot = useCurrentShot();
  const [spin, setSpin] = useState(false);
  const [saved, setSaved] = useState<{ id: string; name: string }[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const act = async (id: string, fn: () => Promise<unknown> | unknown) => {
    setBusy(id);
    try {
      await fn();
    } catch (e) {
      fail(e as Error);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-basic-camera>
      <Section label="Shots">
        <div className="grid grid-cols-3 gap-2">
          {SHOTS.map((s) => (
            <Tile
              key={s.id}
              label={s.label}
              on={shot === s.id}
              busy={busy === s.id}
              tall
              thumbStyle={{ background: "#ffffff" }}
              thumb={<Thumb src={`/shots/${s.id}.webp`} />}
              onClick={() => void act(s.id, () => takeShot(r, s.id))}
              data-shot={s.id}
            />
          ))}
        </div>
      </Section>
      <Section label="Adjust">
        <div className="flex flex-col gap-1.5">
          <ActionRow
            icon="360"
            label={spin ? "Stop spinning" : "Spin slowly"}
            sub="Turns the piece on the spot"
            onClick={() => { const next = !spin; setSpin(next); void r.setAutoRotate(next, 0.6); }}
            trailing={<span className={`h-5 w-9 rounded-full p-0.5 transition-colors ${spin ? "bg-ed-gold" : "bg-ed-border"}`}><span className={`block h-4 w-4 rounded-full bg-white shadow transition-transform ${spin ? "translate-x-4" : ""}`} /></span>}
            data-spin
          />
          <ActionRow icon="straighten" label="Straighten the ring" sub="Stands it upright if the file was saved at an angle" busy={busy === "pose"} onClick={() => void act("pose", () => r.computePose())} />
          <ActionRow icon="fit_screen" label="Fit in view" sub="Centres the piece and fills the frame" busy={busy === "fit"} onClick={() => void act("fit", () => r.autoFit())} />
        </div>
      </Section>
      <Section
        label="Your views"
        aside={
          <button
            className="flex items-center gap-1 text-[12px] font-semibold text-ed-gold hover:underline"
            onClick={() =>
              void act("save", async () => {
                const v = await r.addCameraView(`View ${saved.length + 1}`);
                setSaved((s) => [...s, { id: v.id, name: v.name ?? `View ${s.length + 1}` }]);
              })
            }
            data-save-view
          >
            <Icon name="add" size={16} /> Save this view
          </button>
        }
      >
        {saved.length === 0 ? (
          <Note>Found a good angle? Save it and come back to it with one click.</Note>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {saved.map((v) => (
              <button
                key={v.id}
                onClick={() => void act(`view:${v.id}`, () => r.flyToCameraView(v.id, 600))}
                className="h-[30px] rounded-lg border border-ed-border px-3 text-[12px] font-medium text-ed-text-2 hover:border-ed-line hover:text-ed-text"
              >
                {v.name}
              </button>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

export type { AppliedStep };
